import { computed, ref, watch } from 'vue'
import { defineStore } from 'pinia'
import { getTemplateData } from '@/utils/dom'
import { useBundleStore } from '@/stores/bundle'
import { useCartStore } from '@/stores/cart'
import { useToastStore } from '@/plugins/toast'
import { useAmplitude } from '@/services/amplitude'
import type { Product, Variant } from '@/types/shopify'

const getProteinPackSize = (sku?: string | null) => Number(sku?.match(/(\d+)$/)?.[1])

export const useProteinPackUpsellStore = defineStore('protein-pack/upsell', () => {
  const bundleStore = useBundleStore()
  const cartStore = useCartStore()
  const toast = useToastStore()

  // ── Product data (injected by Liquid) ─────────────────────────────────────────

  const product = ref(getTemplateData<Product>('#json-protein-pack'))
  const meta = ref(getTemplateData<Product>('#json-protein-pack-meta'))

  // ── Cart-derived state ────────────────────────────────────────────────────────

  const cartItem = computed(() => cartStore.nonBundleItems.find(i => i.handle === 'protein-pack'))
  const cartItemSize = computed(() => getProteinPackSize(cartItem.value?.sku ?? null))

  const isPending = computed(() => !!cartItem.value)
  const showCard = computed(() => !!cartItem.value)

  const isEligible = computed(() =>
    !!product.value &&
    bundleStore.validSize &&
    !cartItem.value
  )

  /**
   * True when there's a protein pack in the cart but its size no longer
   * matches the current (locked) bundle size.
   */
  const sizeMismatch = computed(() =>
    !!cartItem.value &&
    bundleStore.validSize &&
    bundleStore.size !== cartItemSize.value
  )

  // ── Variant resolution ────────────────────────────────────────────────────────

  /** The protein pack variant matching the current bundle size */
  const pendingVariant = computed((): Variant | undefined => {
    if (!product.value?.variants) return undefined
    return product.value.variants.find(v => getProteinPackSize(v.sku) === bundleStore.size)
      ?? product.value.variants[0]
  })

  /** The variant currently in the cart */
  const stagedVariant = computed((): Variant | undefined => {
    if (!product.value?.variants || !cartItem.value) return undefined
    return product.value.variants.find(v => v.id === cartItem.value!.variant_id)
      ?? product.value.variants[0]
  })

  const upsellPriceCents = computed(() => pendingVariant.value?.price ?? 0)

  /** Price difference between new size variant and the one currently in cart */
  const priceDelta = computed(() =>
    (pendingVariant.value?.price ?? 0) - (stagedVariant.value?.price ?? 0)
  )

  /** Pack count shown in the banner ("Add X Packs") */
  const displayPackCount = computed(() => bundleStore.size ?? 0)

  /** Pack count shown in the card (what's actually in the cart) */
  const stagedAtSize = computed(() => cartItemSize.value)

  const nutritionImage = computed(() => meta.value?.nutritionImage)
  const ingredients = computed(() => meta.value?.ingredients ?? product.value?.tags ?? [])

  // ── Selling plan ──────────────────────────────────────────────────────────────

  const sellingPlanId = computed(() =>
    product.value?.selling_plan_groups
      ?.flatMap(g => g.selling_plans)
      .find(p => p.recurring_deliveries)?.id
  )

  /** Whether the protein pack currently in cart is a subscription */
  const isCartItemSub = computed(() => !!cartItem.value?.selling_plan_allocation)

  // ── Actions ───────────────────────────────────────────────────────────────────

  const loadingMessage = ref<string | null>(null)
  const loading = computed(() => !!loadingMessage.value)
  const { trackCart, trackCartRemove, trackCartUpdate } = useAmplitude()

  const addVariantToCart = async (variantId: number, sellingPlan?: number) => {
    await fetch('/cart/add.json', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1, ...(sellingPlan && { selling_plan: sellingPlan }) })
    })
    await cartStore.load()
  }

  /** Add protein pack, automatically matching the bundle's current plan */
  const add = async () => {
    if (!pendingVariant.value) return
    loadingMessage.value = 'Adding Protein Pack...'
    try {
      const sellingPlan = bundleStore.isSub ? sellingPlanId.value : undefined
      await addVariantToCart(pendingVariant.value.id, sellingPlan)
      toast.alert({ message: `You added ${displayPackCount.value} Protein Packs to your order`, type: 'notice' })
      if (cartStore.cart) {
        await trackCart(cartStore.cart)
      }
    } finally {
      loadingMessage.value = null
    }
  }

  const remove = async () => {
    if (!cartItem.value) return
    loadingMessage.value = 'Removing Protein Pack...'
    try {
      const removedSku = cartItem.value.sku
      const removedSize = cartItemSize.value
      await cartStore.update({ updates: { [cartItem.value.id]: 0 } })
      if (cartStore.cart) {
        await trackCartRemove(cartStore.cart, { 'Product': 'Protein Pack', 'SKU': removedSku, 'Pack Size': removedSize })
      }
    } finally {
      loadingMessage.value = null
    }
  }

  /** Swap the cart item to the variant matching the current bundle size, preserving plan */
  const confirmUpdate = async () => {
    if (!pendingVariant.value || !cartItem.value) return
    loadingMessage.value = 'Updating Protein Pack...'
    try {
      const oldSku = cartItem.value.sku
      const oldSize = cartItemSize.value
      const sellingPlan = isCartItemSub.value ? sellingPlanId.value : undefined
      await cartStore.update({ updates: { [cartItem.value.id]: 0 } })
      await addVariantToCart(pendingVariant.value.id, sellingPlan)
      if (cartStore.cart) {
        await trackCartUpdate(cartStore.cart, {
          'Product': 'Protein Pack',
          'Old SKU': oldSku,
          'Old Pack Size': oldSize,
          'New SKU': pendingVariant.value.sku,
          'New Pack Size': bundleStore.size
        })
      }
    } finally {
      loadingMessage.value = null
    }
  }

  // ── Auto-remove when bundle is emptied ────────────────────────────────────────

  watch(() => bundleStore.totalQuantity, (qty) => {
    if (qty === 0 && cartItem.value) remove()
  })

  // ── Sync plan with bundle plan ────────────────────────────────────────────────

  watch(() => bundleStore.isSub, (isSub) => {
    if (!cartItem.value || !pendingVariant.value) return
    const alreadyMatches = isSub === isCartItemSub.value
    if (alreadyMatches) return
    loadingMessage.value = 'Updating Protein Pack...'
    const sellingPlan = isSub ? sellingPlanId.value : undefined
    cartStore.update({ updates: { [cartItem.value.id]: 0 } })
      .then(() => addVariantToCart(pendingVariant.value!.id, sellingPlan))
      .then(() => {
        if (cartStore.cart) trackCart(cartStore.cart)
      })
      .finally(() => {
        loadingMessage.value = null
      })
  })

  // ── Size mismatch modal ───────────────────────────────────────────────────────

  const showUpdateModal = ref(false)

  watch([ sizeMismatch, () => bundleStore.size ], ([ mismatch ]) => {
    bundleStore.updateLocked = mismatch
    if (mismatch) showUpdateModal.value = true
  }, { immediate: true })

  return {
    product,
    meta,
    nutritionImage,
    ingredients,
    loading,
    loadingMessage,
    cartItem,
    stagedAtSize,
    isEligible,
    isPending,
    showCard,
    sizeMismatch,
    pendingVariant,
    stagedVariant,
    upsellPriceCents,
    priceDelta,
    displayPackCount,
    showUpdateModal,
    sellingPlanId,
    isCartItemSub,
    add,
    remove,
    confirmUpdate
  }
})
